import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  LinearProgress,
  TextField,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Schedule,
  Psychology,
  RateReview,
  Mic,
  Transcribe,
  Calculate,
  Error,
  OpenInNew,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import Stack from '@/components/Stack';
import { Body, Small, Caption } from '@/components/Typography';
import Badge from '@/components/Badge';
import Card from '@/components/Card';
import { ConsolidatedAiAssessment } from '@/services/workersV2API';
import { AssessmentsAPI } from '@/services/assessmentsAPI';

type Props = {
  aiAssessment: ConsolidatedAiAssessment;
  canRetry: boolean;
  skillRequestId: number;
  onUpdate: () => void;
  onError: (title: string, message: string) => void;
};

const STATUS_CONFIG = {
  passed: {
    color: '#10B981',
    bgColor: '#ECFDF5',
    borderColor: '#A7F3D0',
    icon: CheckCircle,
    label: 'Passed',
    badgeStatus: 'theme' as const,
  },
  failed: {
    color: '#EF4444',
    bgColor: '#FEF2F2',
    borderColor: '#FECACA',
    icon: Cancel,
    label: 'Failed',
    badgeStatus: 'danger' as const,
  },
  for_review: {
    color: '#F59E0B',
    bgColor: '#FFFBEB',
    borderColor: '#FDE68A',
    icon: RateReview,
    label: 'Needs Review',
    badgeStatus: 'warning' as const,
  },
  pending: {
    color: '#6B7280',
    bgColor: '#F3F4F6',
    borderColor: '#E5E7EB',
    icon: Schedule,
    label: 'Pending',
    badgeStatus: 'neutral' as const,
  },
};

const PROCESSING_STATUS_CONFIG = {
  pending: { icon: Schedule, label: 'Waiting to start', progress: 0 },
  recording: { icon: Mic, label: 'Recording responses', progress: 25 },
  transcribing: { icon: Transcribe, label: 'Transcribing audio', progress: 50 },
  scoring: { icon: Calculate, label: 'AI scoring in progress', progress: 75 },
  completed: { icon: CheckCircle, label: 'Assessment complete', progress: 100 },
  failed: { icon: Error, label: 'Assessment failed', progress: 100 },
};

const AIAssessmentSection = ({
  aiAssessment,
  canRetry,
  skillRequestId,
  onUpdate,
  onError,
}: Props) => {
  const [processing, setProcessing] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState<{
    open: boolean;
    action: 'pass' | 'fail';
  }>({ open: false, action: 'pass' });
  const [reviewComment, setReviewComment] = useState('');

  const statusConfig =
    STATUS_CONFIG[aiAssessment.final_status as keyof typeof STATUS_CONFIG] ||
    STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;

  const processingConfig =
    PROCESSING_STATUS_CONFIG[
      aiAssessment.status as keyof typeof PROCESSING_STATUS_CONFIG
    ] || PROCESSING_STATUS_CONFIG.pending;
  const ProcessingIcon = processingConfig.icon;

  const isInProgress =
    aiAssessment.status !== 'completed' && aiAssessment.status !== 'failed';
  const needsReview = aiAssessment.final_status === 'for_review';

  const handleReview = async (passed: boolean) => {
    setProcessing(true);
    setShowReviewDialog({ open: false, action: 'pass' });

    try {
      const response = await AssessmentsAPI.reviewAssessment(
        aiAssessment.external_id,
        {
          final_status: passed ? 'passed' : 'failed',
          notes: reviewComment || undefined,
          override: true,
        },
      );

      if (response.success) {
        setReviewComment('');
        onUpdate();
      } else {
        onError(
          'Error',
          `Failed to ${passed ? 'approve' : 'reject'} assessment`,
        );
      }
    } catch (error) {
      console.error('Error reviewing AI assessment:', error);
      onError('Error', `Failed to ${passed ? 'approve' : 'reject'} assessment`);
    } finally {
      setProcessing(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <>
      <Card>
        <Card.Section>
          {/* Header */}
          <Stack
            style={{
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <Stack gap={8} style={{ alignItems: 'center' }}>
              <Psychology style={{ color: '#8B5CF6', fontSize: '20px' }} />
              <Body weight="semibold">AI Assessment</Body>
            </Stack>
            <Link
              to={`/ai_assessments?externalId=${aiAssessment.external_id}`}
              style={{ textDecoration: 'none' }}
            >
              <Button
                variant="text"
                size="small"
                endIcon={<OpenInNew style={{ fontSize: '16px' }} />}
              >
                View Details
              </Button>
            </Link>
          </Stack>

          {/* Main Content Card */}
          <div
            style={{
              backgroundColor: statusConfig.bgColor,
              border: `1px solid ${statusConfig.borderColor}`,
              borderRadius: '12px',
              padding: '16px',
            }}
          >
            {/* Status Header */}
            <Stack
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <Stack gap={8} style={{ alignItems: 'center' }}>
                <StatusIcon
                  style={{ color: statusConfig.color, fontSize: '24px' }}
                />
                <Body weight="semibold">
                  {needsReview
                    ? 'Review Required'
                    : aiAssessment.final_status === 'passed'
                      ? 'Assessment Passed'
                      : aiAssessment.final_status === 'failed'
                        ? 'Assessment Failed'
                        : 'Assessment In Progress'}
                </Body>
              </Stack>
              <Stack gap={8}>
                <Badge
                  label={statusConfig.label}
                  status={statusConfig.badgeStatus}
                />
                {aiAssessment.final_status === 'failed' && (
                  <Badge
                    label={canRetry ? 'Can Retry' : 'Retry Blocked'}
                    status={canRetry ? 'warning' : 'neutral'}
                  />
                )}
              </Stack>
            </Stack>

            {/* Processing Status (if in progress) */}
            {isInProgress && (
              <div
                style={{
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px',
                }}
              >
                <Stack
                  gap={12}
                  style={{ alignItems: 'center', marginBottom: '12px' }}
                >
                  <ProcessingIcon
                    style={{
                      color: '#6366F1',
                      fontSize: '20px',
                      animation:
                        aiAssessment.status !== 'completed' &&
                        aiAssessment.status !== 'failed'
                          ? 'pulse 2s infinite'
                          : 'none',
                    }}
                  />
                  <Small weight="semibold">{processingConfig.label}</Small>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={processingConfig.progress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: '#E5E7EB',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#6366F1',
                      borderRadius: 4,
                    },
                  }}
                />
              </div>
            )}

            {/* Score Display */}
            {aiAssessment.total_score !== null && (
              <div
                style={{
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px',
                  textAlign: 'center',
                }}
              >
                <Small color="lighter" style={{ marginBottom: '4px' }}>
                  AI Score
                </Small>
                <div
                  style={{
                    fontSize: '32px',
                    fontWeight: 'bold',
                    color:
                      aiAssessment.final_status === 'passed'
                        ? '#10B981'
                        : aiAssessment.final_status === 'failed'
                          ? '#EF4444'
                          : '#F59E0B',
                  }}
                >
                  {aiAssessment.total_score.toFixed(1)}%
                </div>
                {needsReview && (
                  <Caption color="lighter" style={{ marginTop: '4px' }}>
                    Score is borderline - manual review required
                  </Caption>
                )}
              </div>
            )}

            {/* Metadata */}
            <Stack vertical gap={8} style={{ marginBottom: '16px' }}>
              {aiAssessment.started_at && (
                <Stack style={{ justifyContent: 'space-between' }}>
                  <Small color="lighter">Started</Small>
                  <Small>{formatDateTime(aiAssessment.started_at)}</Small>
                </Stack>
              )}
              {aiAssessment.completed_at && (
                <Stack style={{ justifyContent: 'space-between' }}>
                  <Small color="lighter">Completed</Small>
                  <Small>{formatDateTime(aiAssessment.completed_at)}</Small>
                </Stack>
              )}
              {aiAssessment.reviewed_at && (
                <Stack style={{ justifyContent: 'space-between' }}>
                  <Small color="lighter">Reviewed</Small>
                  <Small>
                    {formatDate(aiAssessment.reviewed_at)}
                    {aiAssessment.reviewed_by_name &&
                      ` by ${aiAssessment.reviewed_by_name}`}
                  </Small>
                </Stack>
              )}
            </Stack>

            {/* Review Action Buttons (for "for_review" status) */}
            {needsReview && (
              <Stack vertical gap={12}>
                <div
                  style={{
                    backgroundColor: '#FEF3C7',
                    borderRadius: '8px',
                    padding: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <RateReview style={{ color: '#D97706', fontSize: '20px' }} />
                  <Small weight="semibold" style={{ color: '#92400E' }}>
                    This assessment needs your review. Please approve or reject
                    based on the score and worker's performance.
                  </Small>
                </div>
                <Stack gap={12}>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={
                      processing ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <CheckCircle />
                      )
                    }
                    onClick={() =>
                      setShowReviewDialog({ open: true, action: 'pass' })
                    }
                    disabled={processing}
                    style={{ flex: 1 }}
                  >
                    Approve Assessment
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<Cancel />}
                    onClick={() =>
                      setShowReviewDialog({ open: true, action: 'fail' })
                    }
                    disabled={processing}
                    style={{ flex: 1 }}
                  >
                    Reject
                  </Button>
                </Stack>
              </Stack>
            )}

            {/* Already reviewed status */}
            {aiAssessment.final_status === 'passed' && !isInProgress && (
              <Stack
                gap={8}
                style={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '8px',
                  backgroundColor: '#fff',
                  borderRadius: '6px',
                }}
              >
                <CheckCircle style={{ color: '#10B981', fontSize: '18px' }} />
                <Small color="lighter">
                  {aiAssessment.reviewed_at
                    ? 'Assessment approved after review'
                    : 'Assessment automatically passed'}
                </Small>
              </Stack>
            )}

            {aiAssessment.final_status === 'failed' && !isInProgress && (
              <Stack vertical gap={8}>
                <Stack
                  gap={8}
                  style={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '8px',
                    backgroundColor: '#fff',
                    borderRadius: '6px',
                  }}
                >
                  <Cancel style={{ color: '#EF4444', fontSize: '18px' }} />
                  <Small color="lighter">
                    {aiAssessment.reviewed_at
                      ? 'Assessment rejected after review'
                      : 'Assessment automatically failed'}
                  </Small>
                </Stack>
              </Stack>
            )}
          </div>
        </Card.Section>
      </Card>

      {/* Review Dialog */}
      <Dialog
        open={showReviewDialog.open}
        onClose={() =>
          setShowReviewDialog({ ...showReviewDialog, open: false })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {showReviewDialog.action === 'pass'
            ? 'Approve AI Assessment'
            : 'Reject AI Assessment'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText style={{ marginBottom: '16px' }}>
            {showReviewDialog.action === 'pass'
              ? 'Are you sure you want to approve this assessment? The worker will be able to proceed with their skill application.'
              : 'Are you sure you want to reject this assessment? The worker may need to retake the assessment.'}
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={reviewComment}
            onChange={(e) => setReviewComment(e.target.value)}
            label="Review Comment (optional)"
            placeholder="Add any notes about your decision..."
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setShowReviewDialog({ ...showReviewDialog, open: false })
            }
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleReview(showReviewDialog.action === 'pass')}
            variant="contained"
            color={showReviewDialog.action === 'pass' ? 'success' : 'error'}
            disabled={processing}
          >
            {processing
              ? 'Processing...'
              : showReviewDialog.action === 'pass'
                ? 'Approve'
                : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AIAssessmentSection;
